import { useEffect, useId, useState } from 'react';
import type { ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import Cre8orLogo from '@/components/Cre8orLogo';
import { useAuth } from '@/services/auth';
import {
  canAccessCatalog,
  canAccessOrders,
  canAccessPartners,
  canReadRevenuePlanning,
  canAccessAgents,
  hasCapability,
  AGENTS_AUDIT_CAPABILITY,
  AGENTS_DATASETS_REVIEW_CAPABILITY,
  AGENTS_OPERATIONS_CAPABILITY,
} from '@/services/permissions';
import {
  catalogGroup,
  catalogStandaloneItems,
  cre8orAiNav,
  crmGroup,
  generatedSkusGroup,
  isPathActive,
  operationsNav,
  packagingSetsGroup,
  referenceDataGroup,
  type SidebarAccess,
  type SidebarNavGroup,
  type SidebarNavItem,
  workspaceNav,
} from './sidebarNavigation';

const sectionLabelClass = 'px-3 pt-3 pb-1 text-xs font-semibold text-brand-sidebar-disabled uppercase tracking-wider';
const focusClass = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2 focus-visible:ring-offset-brand-sidebar-bg';

function SectionLabel({ children }: { children: string }) {
  return <p className={sectionLabelClass}>{children}</p>;
}

function accessAllowed(access: SidebarAccess | undefined, flags: AccessFlags): boolean {
  switch (access) {
    case 'catalog':
      return flags.catalog;
    case 'orders':
      return flags.orders;
    case 'partners':
      return flags.partners;
    case 'revenuePlanning':
      return flags.revenuePlanning;
    case 'agents':
      return flags.agents;
    case 'agentAudit':
      return flags.agentAudit;
    case 'agentDatasets':
      return flags.agentDatasets;
    case 'agentOperations':
      return flags.agentOperations;
    case 'authenticated':
    case undefined:
      return true;
  }
}

interface AccessFlags {
  catalog: boolean;
  orders: boolean;
  partners: boolean;
  revenuePlanning: boolean;
  agents: boolean;
  agentAudit: boolean;
  agentDatasets: boolean;
  agentOperations: boolean;
}

function linkClasses(active: boolean, extra = '') {
  return [
    'flex items-center gap-3 px-3 py-2 rounded-full text-sm font-medium transition-colors',
    focusClass,
    active
      ? 'bg-brand-filter-tab-active-bg text-brand-filter-tab-active-text'
      : 'text-brand-sidebar-inactive hover:bg-brand-surface hover:text-brand-text-primary',
    extra,
  ].join(' ');
}

function ActiveNavLink({ item, pathname }: { item: SidebarNavItem; pathname: string }) {
  const active = isPathActive(pathname, item);

  return (
    <NavLink to={item.to} className={linkClasses(active)} aria-current={active ? 'page' : undefined}>
      <item.icon className="h-4 w-4 flex-shrink-0" />
      <span>{item.label}</span>
    </NavLink>
  );
}

function AccordionGroup({
  group,
  pathname,
  children,
  linkLabel,
  compact = false,
}: {
  group: SidebarNavGroup;
  pathname: string;
  children: ReactNode;
  linkLabel?: string;
  compact?: boolean;
}) {
  const active = isPathActive(pathname, group);
  const [manualExpanded, setManualExpanded] = useState<boolean | null>(null);
  const expanded = manualExpanded ?? active;
  const reactId = useId();
  const childrenId = `sidebar-${group.id}-${reactId}`;

  useEffect(() => {
    setManualExpanded(null);
  }, [pathname]);

  if (compact) {
    return (
      <li>
        <button
          type="button"
          aria-expanded={expanded}
          aria-controls={childrenId}
          aria-label={`Toggle ${group.label} menu`}
          onClick={() => setManualExpanded((current) => !(current ?? active))}
          className={[
            'flex items-center justify-between w-full px-3 py-1.5 rounded text-xs font-semibold uppercase tracking-wider transition-colors',
            focusClass,
            active ? 'text-brand-filter-tab-active-text' : 'text-brand-sidebar-disabled hover:text-brand-text-primary',
          ].join(' ')}
        >
          <span className="flex items-center gap-2">
            <group.icon className="h-4 w-4 flex-shrink-0" />
            {group.label}
          </span>
          <ChevronDownIcon className={`h-3 w-3 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </button>
        {expanded && (
          <ul id={childrenId} className="pl-3 space-y-0.5 mt-0.5">
            {children}
          </ul>
        )}
      </li>
    );
  }

  return (
    <li>
      <div
        className={`flex items-center justify-between rounded-full transition-colors ${
          active ? 'bg-brand-filter-tab-active-bg text-brand-filter-tab-active-text' : 'hover:bg-brand-surface group'
        }`}
      >
        <NavLink
          to={group.to}
          className={[
            'flex items-center gap-3 flex-1 px-3 py-2 rounded-l-full text-sm font-medium transition-colors',
            focusClass,
            active
              ? 'bg-brand-filter-tab-active-bg text-brand-filter-tab-active-text'
              : 'text-brand-sidebar-inactive group-hover:text-brand-text-primary',
          ].join(' ')}
          aria-current={active ? 'page' : undefined}
        >
          <group.icon className="h-4 w-4 flex-shrink-0" />
          <span>{linkLabel ?? group.label}</span>
        </NavLink>
        <button
          type="button"
          aria-expanded={expanded}
          aria-controls={childrenId}
          aria-label={`Toggle ${group.label} menu`}
          onClick={() => setManualExpanded((current) => !(current ?? active))}
          className={[
            'px-3 py-2 rounded-r-full text-sm transition-colors',
            focusClass,
            active
              ? 'bg-brand-filter-tab-active-bg text-brand-filter-tab-active-text'
              : 'text-brand-sidebar-inactive group-hover:text-brand-text-primary',
          ].join(' ')}
        >
          <ChevronDownIcon className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </button>
      </div>
      {expanded && (
        <ul id={childrenId} className="pl-4 space-y-0.5 mt-0.5">
          {children}
        </ul>
      )}
    </li>
  );
}

export default function AppSidebar() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const accessFlags: AccessFlags = {
    catalog: canAccessCatalog(user),
    orders: canAccessOrders(user),
    partners: canAccessPartners(user),
    revenuePlanning: canReadRevenuePlanning(user),
    agents: canAccessAgents(user),
    agentAudit: hasCapability(user, AGENTS_AUDIT_CAPABILITY),
    agentDatasets: hasCapability(user, AGENTS_DATASETS_REVIEW_CAPABILITY),
    agentOperations: hasCapability(user, AGENTS_OPERATIONS_CAPABILITY),
  };

  const workspaceItems = workspaceNav.filter((item) => accessAllowed(item.access, accessFlags));
  const crmChildren = crmGroup.children.filter((item) => accessAllowed(item.access, accessFlags));
  const operationItems = operationsNav.filter((item) => accessAllowed(item.access, accessFlags));
  const cre8orAiItems = cre8orAiNav.filter((item) => accessAllowed(item.access, accessFlags));

  return (
    <nav
      className="flex flex-col bg-brand-sidebar-bg border-r border-brand-border rounded-b-2xl pb-6"
      aria-label="Main navigation"
    >
      <div className="px-4 py-4">
        <Cre8orLogo className="w-32 h-auto" />
        <p className="text-xs text-brand-subtitle mt-1 italic">AI Team Platform</p>
      </div>

      <ul className="px-3 py-2 space-y-0.5">
        <li>
          <SectionLabel>Workspace</SectionLabel>
          <ul className="space-y-0.5">
            {workspaceItems.map((item) => (
              <li key={item.id}>
                <ActiveNavLink item={item} pathname={pathname} />
              </li>
            ))}
          </ul>
        </li>

        <li className="my-2 border-t border-brand-border" aria-hidden="true" />

        <li>
          <SectionLabel>CRM</SectionLabel>
          <ul className="space-y-0.5">
            <AccordionGroup group={crmGroup} pathname={pathname}>
              {crmChildren.map((item) => (
                <li key={item.id}>
                  <ActiveNavLink item={item} pathname={pathname} />
                </li>
              ))}
            </AccordionGroup>
          </ul>
        </li>

        {operationItems.length > 0 && (
          <>
            <li className="my-2 border-t border-brand-border" aria-hidden="true" />
            <li>
              <SectionLabel>Operations</SectionLabel>
              <ul className="space-y-0.5">
                {operationItems.map((item) => (
                  <li key={item.id}>
                    <ActiveNavLink item={item} pathname={pathname} />
                  </li>
                ))}
              </ul>
            </li>
          </>
        )}

        {cre8orAiItems.length > 0 && (
          <>
            <li className="my-2 border-t border-brand-border" aria-hidden="true" />
            <li>
              <SectionLabel>Cre8or AI</SectionLabel>
              <ul className="space-y-0.5">
                {cre8orAiItems.map((item) => (
                  <li key={item.id}>
                    <ActiveNavLink item={item} pathname={pathname} />
                  </li>
                ))}
              </ul>
            </li>
          </>
        )}

        {accessFlags.catalog && (
          <>
            <li className="my-2 border-t border-brand-border" aria-hidden="true" />
            <li>
              <SectionLabel>Catalog</SectionLabel>
              <ul className="space-y-0.5">
                <AccordionGroup group={catalogGroup} pathname={pathname} linkLabel="Catalog Home">
                  <AccordionGroup group={generatedSkusGroup} pathname={pathname}>
                    {generatedSkusGroup.children.slice(0, 3).map((item) => (
                      <li key={item.id}>
                        <ActiveNavLink item={item} pathname={pathname} />
                      </li>
                    ))}
                    <AccordionGroup group={packagingSetsGroup} pathname={pathname}>
                      {packagingSetsGroup.children.map((item) => (
                        <li key={item.id}>
                          <ActiveNavLink item={item} pathname={pathname} />
                        </li>
                      ))}
                    </AccordionGroup>
                    {generatedSkusGroup.children.slice(3).map((item) => (
                      <li key={item.id}>
                        <ActiveNavLink item={item} pathname={pathname} />
                      </li>
                    ))}
                  </AccordionGroup>

                  {catalogStandaloneItems.map((item) => (
                    <li key={item.id}>
                      <ActiveNavLink item={item} pathname={pathname} />
                    </li>
                  ))}

                  <AccordionGroup group={referenceDataGroup} pathname={pathname} compact>
                    {referenceDataGroup.children.map((item) => (
                      <li key={item.id}>
                        <ActiveNavLink item={item} pathname={pathname} />
                      </li>
                    ))}
                  </AccordionGroup>
                </AccordionGroup>
              </ul>
            </li>
          </>
        )}

      </ul>
    </nav>
  );
}
